// src/components/vault/QuickUploadDialog.jsx  (UPDATED: inline loader)
import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, MenuItem, Button, CircularProgress, Typography } from "@mui/material";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const CATEGORIES = [
    { key: "legal", label: "Legal & Identity Documents" },
    { key: "education", label: "Education & Board Certification Documents" },
    { key: "licenses", label: "Medical Licenses" },
    { key: "resuscitation", label: "Resuscitation Documents" },
    { key: "logs", label: "Procedure/Case Logs" },
    { key: "employment", label: "Employment Documents" },
    { key: "health", label: "Health & Immunization Documents" },
    { key: "other", label: "Other/Miscellaneous Documents" },
];

export default function QuickUploadDialog({ open, onClose, onSubmit }) {
    const [file, setFile] = React.useState(null);
    const [name, setName] = React.useState("");
    const [category, setCategory] = React.useState("other");
    const [expires, setExpires] = React.useState("");
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState("");

    React.useEffect(() => {
        if (open) { setFile(null); setName(""); setCategory("other"); setExpires(""); setSaving(false); setError(""); }
    }, [open]);

    const handleUpload = async () => {
        setSaving(true);
        try {
            await onSubmit?.({ file, name: name.trim(), category, expiresAt: expires || null });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Add Document</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={1.5}>
                    <TextField select size="small" label="Category" value={category} onChange={(e) => setCategory(e.target.value)} disabled={saving}>
                        {CATEGORIES.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
                    </TextField>
                    <TextField label="Document Name" size="small" value={name} onChange={(e) => setName(e.target.value)} disabled={saving} required />
                    <Button variant="outlined" component="label" disabled={saving}>
                        {file ? "Change File" : "Choose File"}
                        <input
                            type="file"
                            hidden
                            onChange={(e) => {
                                const selectedFile = e.target.files?.[0];
                                if (selectedFile) {
                                    if (selectedFile.size > MAX_FILE_SIZE) {
                                        setError("File is too large. Maximum file size is 5MB.");
                                        setFile(null);
                                    } else {
                                        setError("");
                                        setFile(selectedFile);
                                    }
                                } else {
                                    setFile(null);
                                }
                                e.target.value = "";
                            }}
                            accept=".pdf,.png,.jpg,.jpeg"
                        />
                    </Button>
                    {file && <Typography variant="body2" color="textSecondary">{file.name}</Typography>}
                    {error && <Typography variant="body2" color="error">{error}</Typography>}
                    <TextField
                        size="small" type="date" label="Expiration date (optional)" InputLabelProps={{ shrink: true }}
                        value={expires} onChange={(e) => setExpires(e.target.value)} disabled={saving}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" disabled={!file || !name.trim() || saving} onClick={handleUpload}>
                    {saving ? <CircularProgress size={18} sx={{ color: "white" }} /> : "Upload"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}